import React from 'react';

interface BaseInputProps {
  label?: string;
  error?: string;
  helperText?: string;
  required?: boolean;
  fullWidth?: boolean;
}

interface TextInputProps extends BaseInputProps, Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  type?: 'text' | 'email' | 'password' | 'number' | 'url' | 'tel';
  as?: 'input';
}

interface TextareaProps extends BaseInputProps, React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  as: 'textarea';
  rows?: number;
}

interface SelectProps extends BaseInputProps, React.SelectHTMLAttributes<HTMLSelectElement> {
  as: 'select';
  options: Array<{ value: string | number; label: string }>;
}

type InputProps = TextInputProps | TextareaProps | SelectProps;

export default function Input(props: InputProps): JSX.Element {
  const { label, error, helperText, required, fullWidth, as = 'input', className = '', ...rest } = props;

  const wrapperClass = ['input-wrapper', fullWidth ? 'input-full' : '', className].filter(Boolean).join(' ');
  const inputClass = ['input', error ? 'input-error' : ''].filter(Boolean).join(' ');

  const renderInput = () => {
    if (as === 'textarea') {
      const { rows = 4, ...textareaProps } = rest as TextareaProps;
      return <textarea className={inputClass} rows={rows} {...textareaProps} />;
    }

    if (as === 'select') {
      const { options, ...selectProps } = rest as SelectProps;
      return (
        <select className={inputClass} {...selectProps}>
          {options.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }

    const { type = 'text', ...inputProps } = rest as TextInputProps;
    return <input type={type} className={inputClass} {...inputProps} />;
  };

  return (
    <div className={wrapperClass}>
      {label && (
        <label className="input-label">
          {label}
          {required && <span className="input-required">*</span>}
        </label>
      )}
      {renderInput()}
      {error && <span className="input-error-text">{error}</span>}
      {helperText && !error && <span className="input-helper-text">{helperText}</span>}
    </div>
  );
}
